import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { baseUrl } from './api';
import { getToken } from './auth';
import { queryKeys } from './queries';

interface PatientAnalysisEvent {
  type: 'ocr_complete' | 'transcription_complete' | 'reanalyze_complete';
  patientId: number;
  timestamp: string;
}

const RECONNECT_DELAY_MS = 5_000;
const MAX_RECONNECT_DELAY_MS = 30_000;

/**
 * SSE hook that listens to /patients/analysis-events for real-time OCR updates.
 * On receiving events, invalidates patients, images, and dashboard queries
 * so the UI reflects new data without a full page refresh.
 */
export function usePatientAnalysisEvents(enabled = true): void {
  const queryClient = useQueryClient();
  const retriesRef = useRef(0);

  useEffect(() => {
    if (!enabled) return;

    let es: EventSource | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let disposed = false;

    function connect(): void {
      if (disposed) return;

      const token = getToken();
      if (!token) return;

      const url = `${baseUrl}/patients/analysis-events?token=${encodeURIComponent(token)}`;
      es = new EventSource(url);

      es.onopen = () => {
        retriesRef.current = 0;
      };

      es.onmessage = (e: MessageEvent<string>) => {
        let event: PatientAnalysisEvent;
        try {
          event = JSON.parse(e.data) as PatientAnalysisEvent;
        } catch {
          return;
        }

        if (!event.type || !event.patientId) return;

        void queryClient.invalidateQueries({ queryKey: queryKeys.patients() });
        void queryClient.invalidateQueries({ queryKey: queryKeys.images() });
        void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard() });
        void queryClient.invalidateQueries({
          queryKey: queryKeys.patientDetail(event.patientId),
        });
        void queryClient.invalidateQueries({
          queryKey: queryKeys.patientEyeSummary(event.patientId),
        });
      };

      es.onerror = () => {
        es?.close();
        es = null;

        if (disposed) return;

        const delay = Math.min(
          RECONNECT_DELAY_MS * 2 ** retriesRef.current,
          MAX_RECONNECT_DELAY_MS,
        );
        retriesRef.current += 1;
        reconnectTimer = setTimeout(connect, delay);
      };
    }

    connect();

    return () => {
      disposed = true;
      es?.close();
      if (reconnectTimer) clearTimeout(reconnectTimer);
    };
  }, [enabled, queryClient]);
}
